import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

interface CouponForPoster {
    id: number;
    name: string;
    code: string;
    discount_type?: '' | 'percent' | 'amount' | 'fixed_price' | null;
    discount_value?: number | null;
    starts_at?: string | null;
    ends_at?: string | null;
}

interface Props {
    open: boolean;
    onClose: () => void;
    coupon: CouponForPoster | null;
    // /c/xxx の短縮URL（あれば QR に使う）
    linkUrl?: string;
}

// 画像読み込み（InviteDialog と同じ感じ）
const loadImage = (src: string) =>
    new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
    });

// 折り返し描画（長文用）
const drawTextWrap = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
    lineHeight: number,
    align: CanvasTextAlign = 'center'
) => {
    ctx.textAlign = align;
    const paragraphs = (text || '').split(/\n/);
    for (const para of paragraphs) {
        const words = para.split(/(\s+)/);
        let line = '';
        for (let i = 0; i < words.length; i++) {
            const test = line + words[i];
            const w = ctx.measureText(test).width;
            if (w > maxWidth && line !== '') {
                ctx.fillText(line, x, y);
                line = words[i].trimStart();
                y += lineHeight;
            } else {
                line = test;
            }
        }
        if (line) ctx.fillText(line, x, y);
        y += lineHeight;
    }
    return y;
};

const formatDateJP = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}年${m}月${day}日`;
};

// 割引部分だけ（返金は含めない）
const buildDiscountText = (
    coupon: CouponForPoster | null,
    lang: 'ja' | 'zh',
    t: (k: string, opt?: any) => string | undefined
) => {
    if (!coupon || !coupon.discount_type || coupon.discount_value == null) {
        // デフォルト
        return lang === 'zh'
            ? (t('admin.coupons.poster.defaults.discount_zh') as string) || '5% OFF'
            : (t('admin.coupons.poster.defaults.discount') as string) || '5% OFF';
    }

    const v = coupon.discount_value;
    if (coupon.discount_type === 'percent') {
        // 5% OFF
        return `${v}% OFF`;
    }
    if (coupon.discount_type === 'amount') {
        // 1000円OFF / 立减1000円
        if (lang === 'zh') {
            return `${v.toLocaleString()}円 OFF`;
        }
        return `${v.toLocaleString()}円 OFF`;
    }
    if (coupon.discount_type === 'fixed_price') {
        if (lang === 'zh') {
            return `特別価格 ${v.toLocaleString()}円`;
        }
        return `特別価格 ${v.toLocaleString()}円`;
    }
    return '5% OFF';
};

const AdminCouponPosterDialog: React.FC<Props> = ({ open, onClose, coupon, linkUrl }) => {
    const { t, i18n } = useTranslation();
    const lang: 'ja' | 'zh' = i18n.language.toLowerCase().startsWith('zh') ? 'zh' : 'ja';

    const [mode, setMode] = useState<'poster' | 'card'>('poster'); // 縦 or 横

    // 編集可能フィールド
    const [titleText, setTitleText] = useState('');
    const [discountText, setDiscountText] = useState('');
    const [codeTitleText, setCodeTitleText] = useState('');
    const [periodText, setPeriodText] = useState('');
    const [qrUrl, setQrUrl] = useState('');
    const [noteText, setNoteText] = useState('');

    const [busy, setBusy] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // 背景画像パス
    const bgPosterSrc = useMemo(
        () => `${process.env.PUBLIC_URL || ''}/images/coupon_poster_bg.png`,
        []
    );
    const bgCardSrc = useMemo(
        () => `${process.env.PUBLIC_URL || ''}/images/coupon_card_bg.png`,
        []
    );

    // 初期値を i18n + coupon から生成
    useEffect(() => {
        if (!open || !coupon) return;

        const defaultTitle =
            (t('admin.coupons.poster.defaults.title') as string) ||
            (lang === 'zh' ? '特别介绍优惠' : '特別紹介割引');

        const defaultCodeTitle =
            (t('admin.coupons.poster.defaults.code_title') as string) ||
            (lang === 'zh' ? '使用代码' : 'ご利用コード');

        const baseDiscount = buildDiscountText(coupon, lang, t);

        let defaultPeriod: string;
        if (coupon.ends_at) {
            const dt = formatDateJP(coupon.ends_at);
            defaultPeriod =
                lang === 'zh'
                    ? (t('admin.coupons.poster.defaults.period_zh', { date: dt }) as string) ||
                    `有效期至：${dt}`
                    : (t('admin.coupons.poster.defaults.period', { date: dt }) as string) ||
                    `有効期限：${dt}まで`;
        } else {
            defaultPeriod =
                lang === 'zh'
                    ? (t('admin.coupons.poster.defaults.period_zh_no_date') as string) ||
                    '有效期至：202X年X月X日'
                    : (t('admin.coupons.poster.defaults.period_no_date') as string) ||
                    '有効期限：202X年X月X日まで';
        }

        const defaultNote =
            (t('admin.coupons.poster.defaults.note') as string) ||
            (lang === 'zh'
                ? '※ 本优惠券可在预约在线诊疗时使用。'
                : '※ 本クーポンはオンライン診療のご予約時にご利用いただけます。');

        const defaultQr =
            linkUrl ||
            `https://lp.smhc.ai/c/${(coupon.code || '').toLowerCase()}`;

        // state セット
        setTitleText(coupon.name || defaultTitle);
        setDiscountText(baseDiscount);
        setCodeTitleText(defaultCodeTitle);
        setPeriodText(defaultPeriod);
        setQrUrl(defaultQr);
        setNoteText(defaultNote);
        setPreviewUrl(null);
        setError(null);
        setMode('poster');
    }, [open, coupon, lang, linkUrl, t]);

    if (!open || !coupon) return null;

    // 画像生成
    const generateImage = async (auto: boolean) => {
        if (!qrUrl || busy || !coupon) return;
        setBusy(true);
        setError(null);
        try {
            const bgSrc = mode === 'poster' ? bgPosterSrc : bgCardSrc;

            // QR dataURL 生成（InviteDialog と同じ）
            const { toDataURL } = await import('qrcode');
            const qrDataUrl = await toDataURL(qrUrl, { width: 400, margin: 2 });

            // 背景読み込み
            const [bgImg, qrImg] = await Promise.all([
                loadImage(bgSrc),
                loadImage(qrDataUrl),
            ]);

            // 固定幅（解像度抑制）＋ アスペクト比維持（歪み防止）
            // ユーザー要望により解像度アップ
            const POSTER_TARGET_W = 1200;
            const CARD_TARGET_W = 1600;

            const targetW = mode === 'poster' ? POSTER_TARGET_W : CARD_TARGET_W;
            const aspect = bgImg.width / bgImg.height;
            const targetH = targetW / aspect;

            const canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;

            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('canvas-context-unavailable');

            // 背景（アスペクト比を維持してフィット）
            ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);

            const W = canvas.width;
            const H = canvas.height;

            if (mode === 'poster') {
                // ===== 縦長ポスターレイアウト =====
                // タイトル位置をさらに下げて調整

                ctx.textBaseline = 'top';
                ctx.textAlign = 'center';

                // タイトル (25%) - さらに下げる
                ctx.fillStyle = '#333333';
                const titleSize = Math.floor(W * 0.06);
                ctx.font = `600 ${titleSize}px "Noto Serif JP", "Noto Serif SC", "Hiragino Mincho ProN", serif`;
                drawTextWrap(ctx, titleText, W / 2, H * 0.25, W * 0.9, titleSize * 1.4);

                // 割引 (35%) - 連動して下げる
                ctx.fillStyle = '#8B1C33';
                const discountSize = Math.floor(W * 0.13);
                ctx.font = `700 ${discountSize}px "Noto Serif JP", "Noto Serif SC", "Hiragino Mincho ProN", serif`;
                drawTextWrap(ctx, discountText, W / 2, H * 0.35, W * 0.9, discountSize * 1.2);

                // クーポンコード枠 (52%)
                const codeBoxW = W * 0.7;
                const codeBoxH = H * 0.08;
                const codeBoxY = H * 0.52;
                const codeBoxX = (W - codeBoxW) / 2;

                ctx.fillStyle = '#ffffff';
                ctx.strokeStyle = '#C5A065';
                ctx.lineWidth = 5;
                const r = 8;
                ctx.beginPath();
                ctx.roundRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH, r);
                ctx.fill();
                ctx.stroke();

                // コード本文
                ctx.fillStyle = '#000000';
                const codeSize = Math.floor(codeBoxH * 0.65);
                ctx.font = `700 ${codeSize}px "Roboto", "Helvetica", "Arial", sans-serif`;
                ctx.textBaseline = 'middle';
                ctx.fillText(coupon.code, W / 2, codeBoxY + codeBoxH / 2 + 4);
                ctx.textBaseline = 'top';

                // コードラベル (枠の下)
                const labelSize = Math.floor(W * 0.025);
                ctx.font = `400 ${labelSize}px "Noto Sans JP", "sans-serif"`;
                ctx.fillStyle = '#444444';
                ctx.fillText(codeTitleText, W / 2, codeBoxY + codeBoxH + (H * 0.02));

                // QRコード (70%)
                const qrSize = W * 0.22;
                const qrX = (W - qrSize) / 2;
                const qrY = H * 0.70;
                ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

                // URL
                const urlSize = Math.floor(W * 0.022);
                ctx.font = `400 ${urlSize}px "Roboto", "Helvetica", "Arial", sans-serif`;
                ctx.fillStyle = '#333333';
                ctx.fillText(qrUrl, W / 2, qrY + qrSize + (H * 0.02));

                // 備考 (92%)
                if (noteText) {
                    const noteSize = Math.floor(W * 0.02);
                    ctx.font = `400 ${noteSize}px "Noto Sans JP", "sans-serif"`;
                    ctx.fillStyle = '#666666';
                    drawTextWrap(ctx, noteText, W / 2, H * 0.92, W * 0.85, noteSize * 1.5);
                }

            } else {
                // ===== 横長カードレイアウト =====
                // フォントサイズをさらに縮小

                ctx.textBaseline = 'top';
                ctx.textAlign = 'center';

                // タイトル (15%)
                ctx.fillStyle = '#333333';
                const titleSize = Math.floor(W * 0.03); // 0.035 -> 0.03
                ctx.font = `600 ${titleSize}px "Noto Serif JP", "Noto Serif SC", "Hiragino Mincho ProN", serif`;
                drawTextWrap(ctx, titleText, W / 2, H * 0.15, W * 0.9, titleSize * 1.4);

                // 割引 (28%)
                ctx.fillStyle = '#8B1C33';
                const discountSize = Math.floor(W * 0.08); // 0.09 -> 0.08
                ctx.font = `700 ${discountSize}px "Noto Serif JP", "Noto Serif SC", "Hiragino Mincho ProN", serif`;
                drawTextWrap(ctx, discountText, W / 2, H * 0.28, W * 0.9, discountSize * 1.2);

                // クーポンコード枠 (55%)
                const codeBoxW = W * 0.35; // 0.40 -> 0.35
                const codeBoxH = H * 0.12; // 0.14 -> 0.12
                const codeBoxY = H * 0.55;
                const codeBoxX = (W - codeBoxW) / 2;

                ctx.fillStyle = '#ffffff';
                ctx.strokeStyle = '#C5A065';
                ctx.lineWidth = 5;
                const r = 8;
                ctx.beginPath();
                ctx.roundRect(codeBoxX, codeBoxY, codeBoxW, codeBoxH, r);
                ctx.fill();
                ctx.stroke();

                // コード本文
                ctx.fillStyle = '#000000';
                const codeSize = Math.floor(codeBoxH * 0.60);
                ctx.font = `700 ${codeSize}px "Roboto", "Helvetica", "Arial", sans-serif`;
                ctx.textBaseline = 'middle';
                ctx.fillText(coupon.code, W / 2, codeBoxY + codeBoxH / 2 + 4);
                ctx.textBaseline = 'top';

                // コードラベル + 有効期限 (枠の下)
                const labelSize = Math.floor(W * 0.012); // 0.014 -> 0.012
                ctx.font = `400 ${labelSize}px "Noto Sans JP", "sans-serif"`;
                ctx.fillStyle = '#444444';
                const combinedLabel = `${codeTitleText}（${periodText}）`;
                ctx.fillText(combinedLabel, W / 2, codeBoxY + codeBoxH + (H * 0.02));

                // QRコード & URL (78%)
                const qrSize = H * 0.15; // 0.16 -> 0.15
                const qrY = H * 0.78;

                // URL幅計測
                const urlSize = Math.floor(W * 0.012); // 0.014 -> 0.012
                ctx.font = `400 ${urlSize}px "Roboto", "Helvetica", "Arial", sans-serif`;
                const urlWidth = ctx.measureText(qrUrl).width;
                const gap = 20;
                const totalW = qrSize + gap + urlWidth;
                const startX = (W - totalW) / 2;

                // QR
                ctx.drawImage(qrImg, startX, qrY, qrSize, qrSize);

                // URL
                ctx.textAlign = 'left';
                ctx.fillStyle = '#333333';
                ctx.textBaseline = 'middle';
                ctx.fillText(qrUrl, startX + qrSize + gap, qrY + qrSize / 2);
            }

            const url = canvas.toDataURL('image/png');
            setPreviewUrl(url);
            if (!auto) {
                // 手動生成時はスクロールさせたい場合ここで追加も可
            }
        } catch (e: any) {
            console.error(e);
            setError(
                (t('admin.coupons.poster.errors.generate_failed') as string) ||
                '画像の生成に失敗しました。'
            );
        } finally {
            setBusy(false);
        }
    };

    const onDownload = () => {
        if (!previewUrl || !coupon) return;
        const a = document.createElement('a');
        a.href = previewUrl;
        a.download = `${coupon.code}_${mode}.png`;
        a.click();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full mx-4 h-[90vh] flex flex-col">
                {/* header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="space-y-1">
                        <div className="text-xs text-gray-500">
                            ID: {coupon.id} / CODE: {coupon.code}
                        </div>
                        <h2 className="text-lg font-semibold">
                            {t('admin.coupons.poster.title') || 'クーポン用ポスター・カード生成'}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-600"
                    >
                        ×
                    </button>
                </div>

                {/* main */}
                <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
                    {/* 左：設定フォーム */}
                    <div className="w-full md:w-80 border-r p-4 space-y-4 overflow-y-auto">
                        {/* layout */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.layout') || 'レイアウト'}
                            </label>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setMode('poster')}
                                    className={classNames(
                                        'flex-1 text-xs px-2 py-1 rounded border',
                                        mode === 'poster'
                                            ? 'bg-rose-50 border-rose-400 text-rose-700'
                                            : 'bg-white'
                                    )}
                                >
                                    {t('admin.coupons.poster.layout.poster') || '縦長ポスター'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setMode('card')}
                                    className={classNames(
                                        'flex-1 text-xs px-2 py-1 rounded border',
                                        mode === 'card'
                                            ? 'bg-rose-50 border-rose-400 text-rose-700'
                                            : 'bg-white'
                                    )}
                                >
                                    {t('admin.coupons.poster.layout.card') || '横長カード'}
                                </button>
                            </div>
                        </div>

                        {/* タイトル */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.title') ||
                                    (lang === 'zh'
                                        ? '标题（例：特别介绍优惠5%A）'
                                        : 'タイトル（例：特別紹介割引5%A）')}
                            </label>
                            <input
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={titleText}
                                onChange={(e) => setTitleText(e.target.value)}
                            />
                        </div>

                        {/* 割引表示 */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.discount') ||
                                    (lang === 'zh' ? '折扣文案（例：5% OFF）' : '割引表示（例：5% OFF）')}
                            </label>
                            <input
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={discountText}
                                onChange={(e) => setDiscountText(e.target.value)}
                            />
                        </div>

                        {/* コード見出し */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.code_title') ||
                                    (lang === 'zh' ? '代码标题' : 'コード見出し')}
                            </label>
                            <input
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={codeTitleText}
                                onChange={(e) => setCodeTitleText(e.target.value)}
                            />
                        </div>

                        {/* 有効期限 */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.period') ||
                                    (lang === 'zh' ? '有效期文案' : '有効期限テキスト')}
                            </label>
                            <input
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={periodText}
                                onChange={(e) => setPeriodText(e.target.value)}
                            />
                        </div>

                        {/* QR URL */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.qr_url') ||
                                    (lang === 'zh' ? '二维码链接 URL' : 'QRコード用リンクURL')}
                            </label>
                            <input
                                className="w-full border rounded px-2 py-1 text-sm"
                                value={qrUrl}
                                onChange={(e) => setQrUrl(e.target.value)}
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                                {`例：https://lp.smhc.ai/c/${coupon.code.toLowerCase()}`}
                            </p>
                        </div>

                        {/* 備考 */}
                        <div>
                            <label className="block text-xs text-gray-500 mb-1">
                                {t('admin.coupons.poster.fields.note') ||
                                    (lang === 'zh' ? '备注/注意事项' : '備考・注意書き')}
                            </label>
                            <textarea
                                className="w-full border rounded px-2 py-1 text-sm min-h-[60px]"
                                value={noteText}
                                onChange={(e) => setNoteText(e.target.value)}
                            />
                        </div>

                        <button
                            type="button"
                            onClick={() => generateImage(false)}
                            disabled={busy}
                            className={classNames(
                                'w-full mt-2 text-sm font-semibold py-2 rounded-lg shadow',
                                busy
                                    ? 'bg-rose-100 text-rose-400 cursor-not-allowed'
                                    : 'bg-rose-600 hover:bg-rose-700 text-white'
                            )}
                        >
                            {busy
                                ? t('admin.coupons.poster.actions.generating') || '生成中…'
                                : t('admin.coupons.poster.actions.generate') || 'プレビュー生成'}
                        </button>
                    </div>

                    {/* 右：プレビュー */}
                    <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 p-4 overflow-auto">
                        <div className="w-full flex-1 flex items-center justify-center">
                            {busy ? (
                                <p className="text-sm text-gray-600">
                                    {t('admin.coupons.poster.actions.generating') || '生成中…'}
                                </p>
                            ) : previewUrl ? (
                                <img
                                    src={previewUrl}
                                    alt="coupon-poster-preview"
                                    className="max-h-[80vh] w-auto max-w-full rounded-xl shadow border bg-white"
                                />
                            ) : (
                                <p className="text-sm text-gray-500">
                                    {t('admin.coupons.poster.hints.no_preview') ||
                                        (lang === 'zh'
                                            ? '请先点击「生成预览」按钮。'
                                            : '「プレビュー生成」を押して画像を作成してください。')}
                                </p>
                            )}
                        </div>
                        {error && (
                            <p className="mt-2 text-xs text-red-600 text-center px-2">{error}</p>
                        )}
                        <div className="mt-4 flex gap-3">
                            <button
                                type="button"
                                onClick={onDownload}
                                disabled={!previewUrl}
                                className={classNames(
                                    'px-4 py-2 rounded-lg text-sm',
                                    previewUrl
                                        ? 'border bg-white hover:bg-gray-100'
                                        : 'border bg-gray-100 text-gray-400 cursor-not-allowed'
                                )}
                            >
                                {t('admin.coupons.poster.actions.download') || '画像をダウンロード'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* footer */}
                <div className="flex justify-end gap-3 px-6 py-4 border-t">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-100"
                    >
                        {t('common.close') || (lang === 'zh' ? '关闭' : '閉じる')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminCouponPosterDialog;
